// /admin → Shuttle: the bus on a map — the loop, its stops, and the position
// every phone is being shown right now.
//
// It draws NOTHING of its own arithmetic. The line, the stop positions along
// it, the bearing the marker points and the "which leg is it on" caption all
// come out of `src/pathfinder/lib/shuttle.js` — the mirrored module the server
// and UMapsMobile already agree through. A map that snapped the bus to the road
// its own way would be a fourth opinion about where the bus is, and the first
// place anybody would look to settle an argument between the other three.
//
// ── Why it looks like this ──────────────────────────────────────────────────
//  · IT IS FED BY THE PAGE, not by a fetch of its own. `position` is the one
//    the stats above it are describing, so the marker and the "Serving now"
//    tile can never disagree — which they would, at 15 s apart, if this box
//    polled /api/shuttle for a prettier refresh rate. A position is only ever
//    as fresh as the tracker's ping anyway.
//  · THE MARKER WALKS THE ROAD between two polls (`pointAt` over an
//    interpolated distance along the loop) rather than jumping. Not decoration:
//    a jump and a GPS glitch look identical, and the whole job of this box is
//    telling a bus that is moving from one that has stopped. A gap longer than
//    a bus could drive in one poll is NOT glided — that is a re-route, a
//    revoked session or a different phone, and pretending it drove there would
//    be inventing a trip.
//  · IT FOLLOWS THE BUS UNTIL YOU DRAG IT. A drag is the one gesture that
//    means "I am looking at something else" — a zoom is not, so the wheel
//    keeps following. Following is never resumed silently: the button says so
//    and the operator presses it, because a map that grabs itself back while
//    somebody is reading the far end of the route is worse than one that sits
//    still.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import {
  AT_STOP_M, buildRoute, forwardDistance, legIndexAt, pointAt, projectOnRoute, routeLine,
} from '../pathfinder/lib/shuttle.js';

const MAPS_KEY = process.env.REACT_APP_MAPS_KEY;
/** Served straight out of `public/`, the same file `_shuttle.js` requires. */
const ROUTE_URL = encodeURI('/Map Info/pathfinder/shuttle_route.json');

const GLIDE_MS = 1400;
/** Further than this since the last poll is a jump, not a drive — see above. */
const MAX_GLIDE_M = 500;
/** What "focused on the bus" means, when the map was showing the whole loop. */
const FOLLOW_ZOOM = 17;

/** The marker's colour says which source put it there, as the tiles do. */
const SOURCE_COLOR = { tracker: '#1a73e8', crowd: '#b06000', simulate: '#8430ce' };

export default function ShuttleMap({ position, serving, seenText }) {
  const divRef = useRef(null);
  const mapRef = useRef(null);
  const busRef = useRef(null);
  /** Where the MARKER is, in metres around the loop — not where the bus is. */
  const atRef = useRef(null);
  const rafRef = useRef(0);
  const focusedRef = useRef(false);

  const [route, setRoute] = useState(null);
  const [routeErr, setRouteErr] = useState('');
  const [following, setFollowing] = useState(true);
  const followRef = useRef(true);
  useEffect(() => { followRef.current = following; }, [following]);

  const { isLoaded, loadError } = useJsApiLoader({ googleMapsApiKey: MAPS_KEY || '' });

  useEffect(() => {
    let alive = true;
    fetch(ROUTE_URL)
      .then((r) => r.json())
      .then((doc) => {
        if (!alive) return;
        const built = buildRoute(doc);
        if (built) setRoute(built);
        else setRouteErr('The route file is there but could not be read as a loop.');
      })
      .catch(() => { if (alive) setRouteErr('The route file could not be loaded.'); });
    return () => { alive = false; };
  }, []);

  const color = SOURCE_COLOR[serving] || '#5f6368';

  // ── The map, its line and its stops: built once, and only once there is a
  //    route to draw. Everything created here is torn down again, because
  //    StrictMode mounts this twice in development and a second set of markers
  //    on the same map would be invisible and permanent.
  useEffect(() => {
    if (!isLoaded || !route || !divRef.current) return undefined;
    const g = window.google.maps;
    const line = routeLine(route);

    const map = new g.Map(divRef.current, {
      center: line[0], zoom: 15, mapTypeId: 'roadmap', tilt: 0,
      disableDefaultUI: true, zoomControl: true, gestureHandling: 'greedy', clickableIcons: false,
    });
    mapRef.current = map;
    atRef.current = null;
    focusedRef.current = false;

    const bounds = new g.LatLngBounds();
    for (const p of line) bounds.extend(p);
    map.fitBounds(bounds, 24);

    const shapes = [
      new g.Polyline({
        map, path: line, clickable: false, zIndex: 1,
        strokeColor: '#1a73e8', strokeOpacity: 0.75, strokeWeight: 5,
      }),
    ];

    // A stop's name has to be readable without hovering it — this is a screen
    // for reading off which stop the bus is between, and a Marker title is
    // only a tooltip. `overlayLayer` so a label can never sit over the bus.
    class StopLabel extends g.OverlayView {
      constructor(at, text) { super(); this.at = at; this.text = text; this.div = null; }
      onAdd() {
        const d = document.createElement('div');
        d.textContent = this.text;
        d.style.cssText = [
          'position:absolute', 'transform:translate(-50%,-210%)', 'white-space:nowrap',
          'background:rgba(255,255,255,0.94)', 'border:1px solid #dadce0', 'border-radius:6px',
          'padding:2px 6px', 'font:700 11px/1.2 system-ui,-apple-system,sans-serif',
          'color:#202124', 'pointer-events:none', 'box-shadow:0 1px 3px rgba(0,0,0,0.16)',
        ].join(';');
        this.div = d;
        this.getPanes().overlayLayer.appendChild(d);
      }
      draw() {
        const p = this.getProjection().fromLatLngToDivPixel(this.at);
        if (p && this.div) { this.div.style.left = `${p.x}px`; this.div.style.top = `${p.y}px`; }
      }
      onRemove() { this.div?.parentNode?.removeChild(this.div); this.div = null; }
    }

    for (const s of route.stops) {
      const at = new g.LatLng(s.lat, s.lng);
      shapes.push(new g.Marker({
        map, position: at, title: s.name, zIndex: 2, clickable: false,
        icon: {
          path: g.SymbolPath.CIRCLE, scale: 5.5,
          fillColor: '#fff', fillOpacity: 1, strokeColor: '#1a73e8', strokeWeight: 2.5,
        },
      }));
      const label = new StopLabel(at, s.name);
      label.setMap(map);
      shapes.push(label);
    }

    const bus = new g.Marker({ map: null, zIndex: 10, clickable: false, title: 'The shuttle' });
    busRef.current = bus;
    shapes.push(bus);

    const drag = map.addListener('dragstart', () => setFollowing(false));

    return () => {
      drag.remove();
      cancelAnimationFrame(rafRef.current);
      for (const s of shapes) s.setMap(null);
      g.event.clearInstanceListeners(map);
      busRef.current = null;
      mapRef.current = null;
    };
  }, [isLoaded, route]);

  // ── The bus itself, on every poll. ────────────────────────────────────────
  //    Declared after the map's effect and keyed on the same two things, so on
  //    the render where the map is built this runs straight after it — the
  //    first bus is drawn then, not a poll later.
  useEffect(() => {
    const map = mapRef.current;
    const bus = busRef.current;
    if (!isLoaded || !map || !bus || !route) return undefined;
    const g = window.google.maps;

    if (!position) { bus.setMap(null); atRef.current = null; return undefined; }

    // `at` is the server's, off the same projection this map draws with; a
    // position that somehow arrives without one is snapped here rather than
    // dropped, because a bus in roughly the right place beats no bus.
    const target = Number.isFinite(position.at)
      ? ((position.at % route.length) + route.length) % route.length
      : projectOnRoute(route, position)?.at;
    if (!Number.isFinite(target)) { bus.setMap(null); return undefined; }

    const from = atRef.current;
    const ahead = from == null ? 0 : forwardDistance(from, target, route.length);
    const glide = ahead > 0 && ahead <= MAX_GLIDE_M ? ahead : 0;
    const started = performance.now();

    const step = (now) => {
      const k = glide ? Math.min(1, (now - started) / GLIDE_MS) : 1;
      const at = glide ? (from + glide * k) % route.length : target;
      atRef.current = at;
      const p = pointAt(route, at);
      const ll = { lat: p.lat, lng: p.lng };
      bus.setPosition(ll);
      bus.setIcon({
        path: g.SymbolPath.FORWARD_CLOSED_ARROW, scale: 6.5, rotation: p.bearing,
        fillColor: color, fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2.5,
      });
      if (bus.getMap() !== map) bus.setMap(map);
      // The first fix the box ever gets is what it zooms to; after that the
      // zoom is whatever the operator left it at.
      if (!focusedRef.current) {
        focusedRef.current = true;
        if (followRef.current) { map.setZoom(FOLLOW_ZOOM); map.setCenter(ll); }
      } else if (followRef.current) {
        map.setCenter(ll);
      }
      if (k < 1) rafRef.current = requestAnimationFrame(step);
    };

    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [isLoaded, position, route, color]);

  const refocus = useCallback(() => {
    setFollowing(true);
    const map = mapRef.current;
    if (!map || !route || atRef.current == null) return;
    const p = pointAt(route, atRef.current);
    if ((map.getZoom() || 0) < FOLLOW_ZOOM) map.setZoom(FOLLOW_ZOOM);
    map.panTo({ lat: p.lat, lng: p.lng });
  }, [route]);

  /** Which leg the served position is on, in the words the leg table uses. */
  const legText = useMemo(() => {
    if (!route || !position || !Number.isFinite(position.at)) return '';
    const n = route.stops.length;
    const i = legIndexAt(route, position.at);
    const from = route.stops[i];
    const to = route.stops[(i + 1) % n];
    const since = forwardDistance(from.at, position.at, route.length);
    if (since < AT_STOP_M) return `at ${from.name}`;
    const until = forwardDistance(position.at, to.at, route.length);
    if (until < AT_STOP_M) return `at ${to.name}`;
    return `${from.name} → ${to.name}`;
  }, [route, position]);

  const problem = !MAPS_KEY
    ? 'Missing REACT_APP_MAPS_KEY — the map cannot load.'
    : loadError ? 'Google Maps did not load.'
    : routeErr || '';

  return (
    <div style={S.section}>
      <div style={S.label}>
        Where the bus is
        <span style={S.labelHint}> · the loop, its stops, and the position every phone is being shown</span>
      </div>

      <div style={S.strip}>
        <span style={{ ...S.dot, background: position ? color : '#dadce0' }} />
        <b>
          {position
            ? (serving === 'simulate' ? 'Simulation' : serving === 'crowd' ? 'Rider reports' : 'Tracker')
            : 'Nothing on the map'}
        </b>
        {position && legText && <span style={S.stripDim}>· {legText}</span>}
        {position && seenText && <span style={S.stripDim}>· {seenText}</span>}
        {position && serving === 'crowd' && position.reports != null && (
          <span style={S.stripDim}>· {position.reports} report{position.reports === 1 ? '' : 's'}</span>
        )}
      </div>

      <div style={S.wrap}>
        <div ref={divRef} style={S.map} />
        {problem ? (
          <div style={S.cover}>{problem}</div>
        ) : !position ? (
          <div style={S.badge}>No position is being served — the bus appears the moment one lands.</div>
        ) : null}
        <button
          type="button"
          style={{ ...S.follow, ...(following ? S.followOn : {}) }}
          onClick={refocus}
          disabled={following || !position}
        >
          {following ? 'Following the bus' : 'Recentre on the bus'}
        </button>
      </div>

      <div style={S.note}>
        The marker is the position <b>/api/shuttle</b> is handing every phone, refreshed with the rest of this
        tab every 15 seconds and walked along the road in between — never fresher than the tracker's last
        ping. The arrow points the way the road runs there. Drag the map and it stops following; the button
        brings it back. The line and stops are the route file the ETAs are measured against.
      </div>
    </div>
  );
}

const S = {
  section: { marginBottom: 16 },
  label: { fontSize: 11, fontWeight: 800, color: '#5f6368', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 },
  labelHint: { textTransform: 'none', letterSpacing: 0, fontWeight: 600 },
  strip: {
    display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
    fontSize: 13, color: '#202124', marginBottom: 8,
  },
  stripDim: { color: '#5f6368' },
  dot: { width: 10, height: 10, borderRadius: 999, display: 'inline-block' },
  wrap: { position: 'relative', borderRadius: 12, overflow: 'hidden', border: '1px solid #dadce0', background: '#f1f3f4' },
  map: { width: '100%', height: 420 },
  cover: {
    position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: '#f1f3f4', color: '#5f6368', fontSize: 13, padding: 16, textAlign: 'center',
  },
  badge: {
    position: 'absolute', top: 10, left: 10, right: 10, margin: '0 auto', maxWidth: 420,
    background: 'rgba(255,255,255,0.95)', border: '1px solid #dadce0', borderRadius: 8,
    padding: '7px 10px', fontSize: 12.5, color: '#5f6368', textAlign: 'center',
  },
  follow: {
    position: 'absolute', right: 12, bottom: 12, font: 'inherit', fontSize: 13, fontWeight: 700,
    background: '#1a73e8', color: '#fff', border: 'none', borderRadius: 999,
    padding: '8px 14px', cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
  },
  followOn: {
    background: 'rgba(255,255,255,0.95)', color: '#137333', cursor: 'default',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
  },
  note: { fontSize: 12, color: '#5f6368', lineHeight: 1.45, marginTop: 8 },
};
